import {
    Fragment,
    useCallback,
    useEffect,
    useMemo,
    useState
} from "react";
import { useSelector } from "react-redux";

import {
    ChartBarIcon,
    ChevronUpIcon,
    LightBulbIcon,
    PencilSquareIcon,
    PlusIcon,
    RocketLaunchIcon,
    TrashIcon,
    XMarkIcon
} from "@heroicons/react/24/outline";
import {
    PinIcon,
    PinSlashIcon,
} from "@primer/octicons-react";

import { selectIsSidebarLarge, selectMemberships } from "../lib/scraper.slice";
import { classNames, unique } from "../lib/utils";
import {
    IWidget,
    IChart,
    IStatsRange,
    ILookupTableBase,
    IDynamicFilter,
    IPinboardElement,
    WidgetType
} from "../lib/backend/extractions.types.generated";
import {
    BackendObj
} from "../lib/backend";

import {
    Chart,
    Indicator
} from "../components/Chart";
import {
    Button
} from "../components/Button";
import { LoadingSpinner, LoadingSpinnerLimit } from "../components/LoadingSpinner";
import { IconButton } from "../components/Button";
import { InvisibleDropdown } from "../components/Dropdown";
import { initStatsRange, StatsRange } from "../components/StatsRange";
import { ConfirmModal } from "../components/ConfirmModal";
import { TimeoutErrorMessageBar } from "../components/ErrorMessageBar";
import { WidgetSidePanel } from "../components/WidgetList";
import { IMembership } from "../lib/types";

export const INDICATOR_WIDGET_TYPES: WidgetType[] = ["sum_number", "count_number"];

function isWidgetWide(widget: IWidget): boolean {
    return widget.type === "date_tags_chart";
}

function WidgetIcon({ widget_type }: { widget_type: WidgetType }) {
    return ["date_count_chart", "date_distribution_chart", "date_tags_chart", "tags_chart", "date_line_chart"].includes(widget_type) ?
        <ChartBarIcon className="h-5 w-5 text-gray-400" /> :
        <LightBulbIcon className="h-5 w-5 text-gray-400" />;
}

// check if widget requires range data or it is current-date-independent
function hasWidgetRange(widget: IWidget): boolean {
    if (widget.type === "funnel_chart") {
        return widget.details.stages.some(stage => stage.date_header_uuid !== undefined);
    }
    return true;
}

function getWidgetSource(widget: IWidget, lookup_tables: ILookupTableBase[], memberships: IMembership[]): string {
    if (widget.type === "funnel_chart") {
        // funnel can have multiple lookup tables
        const widget_lookup_table_uuids = widget.details.stages.map(stage => stage.lookup_table_uuid);
        const widget_lookup_tables = lookup_tables?.filter(lookup_table => widget_lookup_table_uuids.includes(lookup_table.uuid)) ?? [];
        if (widget_lookup_tables.length === 0) { return "/"; }
        const org = memberships?.find(membership => membership.org.uuid === widget_lookup_tables[0].org_uuid);
        const org_name = org ? `[${org.org.name}] ` : "";
        const lookup_table_names = unique(widget_lookup_tables.map(lookup_table => lookup_table.name));
        return `${org_name}${lookup_table_names.join(", ")}`;
    }
    // other widgets can have only one lookup table
    const lookup_table = lookup_tables?.find(lookup_table => lookup_table.uuid === (widget.details as any).lookup_table_uuid);
    const org = memberships?.find(membership => membership.org.uuid === lookup_table?.org_uuid);
    const org_name = org ? `[${org.org.name}] ` : "";
    return lookup_table ? `${org_name}${lookup_table.name}` : "/";
};


type IndicatorWidgetProps = {
    widget: IWidget;
    is_pinned?: boolean;
    is_editable?: boolean;
    onPinToggle?: () => void;
    onRemove?: () => void;
    onEdit?: () => void;
};

export function IndicatorWidget(props: IndicatorWidgetProps) {
    const { widget, is_pinned, is_editable, onPinToggle, onEdit, onRemove } = props;

    const [chart, setChart] = useState<IChart | undefined>(undefined);
    const [range, setRange] = useState<IStatsRange>(initStatsRange(`widget_range_${widget.uuid}`));

    useEffect(() => {
        const loadWidget = async () => {
            const { chart } = await BackendObj.extractions.previewWidget({ widget_uuid: widget.uuid, range, additional_filters: [], skip_cache: false });
            setChart(chart);
        };
        loadWidget();
    }, [widget.uuid, range]);

    const changeRange = (new_range: IStatsRange) => {
        setRange(new_range);
        localStorage.setItem(`widget_range_${widget.uuid}`, new_range);
    }

    return chart !== undefined ? <Indicator
        chart={chart}
        is_pinned={is_pinned}
        is_editable={is_editable}
        range={range}
        range_local_storage_key={`widget_range_${widget.uuid}`}
        onTogglePin={onPinToggle}
        onEdit={onEdit}
        onRemove={onRemove}
        onRangeChange={changeRange}
    /> : null;
}

type WidgetProps = {
    widget: IWidget;
    show_icon: boolean;
    is_pinned?: boolean;
    is_editable?: boolean;
    lookup_tables?: ILookupTableBase[];
    onClose?: () => void;
    onPinToggle?: () => void;
    onEdit?: () => void;
};

export function Widget(props: WidgetProps) {
    const { widget, is_pinned, is_editable, lookup_tables, onClose, onPinToggle, onEdit, show_icon } = props;

    const memberships = useSelector(selectMemberships);

    const [range, setRange] = useState<IStatsRange>(initStatsRange(`widget_range_${widget.uuid}`));
    const [chart, setChart] = useState<IChart | undefined>(undefined);
    const [additional_filters, setAdditionalFilters] = useState<IDynamicFilter[]>([]);
    const show_range = useMemo(() => hasWidgetRange(widget), [widget]);

    useEffect(() => {
        const loadWidget = async () => {
            const { chart } = await BackendObj.extractions.previewWidget({ widget_uuid: widget.uuid, range, additional_filters, skip_cache: false });
            setChart(chart);
        };
        loadWidget();
    }, [widget, range, additional_filters]);

    const changeFilter = (column_uuid: string, value: string | undefined) => {
        let new_additional_filters: IDynamicFilter[] = [];
        if (value === undefined) {
            // delete filter
            new_additional_filters = additional_filters.filter(f => f.column_uuid !== column_uuid);
        } else {
            // update or add filter
            const new_filter: IDynamicFilter = { column_uuid, possible_values: [value] };
            if (additional_filters.some(f => f.column_uuid === column_uuid)) {
                new_additional_filters = additional_filters.map(f => f.column_uuid === column_uuid ? new_filter : f);
            } else {
                new_additional_filters = [...additional_filters, new_filter];
            }
        }
        setAdditionalFilters([...new_additional_filters]);
    }

    const getSelectedFilterValue = (column_uuid: string): string => {
        if (chart === undefined) { return ""; }
        // first find dynamic filter
        const dynamic_filter = chart.dynamic_filters.find(f => f.column_uuid === column_uuid);
        if (dynamic_filter === undefined) { return ""; }
        // then find selected value
        const selected_filter = additional_filters.find(f => f.column_uuid === column_uuid);
        if (selected_filter && selected_filter.possible_values.length > 0) {
            const selected_value = selected_filter.possible_values[0];
            const idx = dynamic_filter.possible_values.findIndex(v => v === selected_value);
            if (idx !== -1) { return `${idx}`; }
        }
        return "";
    }

    return <div key={widget.uuid} className={classNames("bg-white rounded-lg overflow-hidden shadow ring-1 ring-black ring-opacity-5 flex flex-col", isWidgetWide(widget) ? "xl:col-span-2" : "")}>
        <div className="px-4 flex justify-between items-center border-b border-gray-200 bg-gray-50">
            <div className="py-3 flex flex-row items-center gap-x-2 truncate" title={widget.title}>
                {show_icon && <WidgetIcon widget_type={widget.type} />}
                <span className="text-sm font-semibold text-gray-900 truncate">{widget.title}</span>
            </div>
            <div className="flex items-center gap-x-4">
                {show_range && <StatsRange
                    range={range}
                    local_storage_key={`widget_range_${widget.uuid}`}
                    setRange={setRange} />}
                {onClose && <IconButton icon={XMarkIcon} onClick={onClose} title="Close widget" />}
            </div>
        </div>

        <div className="bg-white flex-grow flex flex-col justify-center">
            {chart === undefined && <div className="h-80 flex items-center justify-center text-gray-500">
                <LoadingSpinnerLimit />
            </div>}
            {chart !== undefined && <Fragment>
                <div className="w-full">
                    <Chart widget={widget} chart={chart} is_wide={isWidgetWide(widget)} />
                </div>
                {chart.dynamic_filters.length > 0 && <div className="mt-2 border-t border-gray-200 flex flex-row gap-x-2 h-11">
                    {chart.dynamic_filters.map(({ column_uuid, column_name, possible_values }, idx) => <InvisibleDropdown
                        key={idx}
                        values={[
                            `${column_name?.replace(/^tag /, "") ?? ""}: All`,
                            ...possible_values.map(value => `${column_name?.replace(/^tag /, "") ?? ""}: ${value}`)
                        ]}
                        ids={["", ...possible_values.map((_, idx) => `${idx}`)]}
                        onChange={(value) => changeFilter(column_uuid, value === "" ? undefined : possible_values[parseInt(value)])}
                        selected={getSelectedFilterValue(column_uuid)}
                    />)}
                </div>}
            </Fragment>}
        </div>

        {lookup_tables !== undefined && is_pinned !== undefined && is_editable !== undefined && onPinToggle !== undefined &&
            <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 text-xs text-gray-500 mt-auto">
                <div className="flex flex-row items-center gap-x-2">
                    <span className="truncate">{getWidgetSource(widget, lookup_tables, memberships)}</span>
                    <div className="flex-grow" />
                    <IconButton
                        icon={is_pinned ? PinIcon : PinSlashIcon}
                        disabled={!is_editable}
                        onClick={onPinToggle}
                        title={is_pinned ? "Pin to dashboard" : "Unpin from dashboard"}
                        size="tiny"
                    />
                    {onEdit && <IconButton
                        icon={PencilSquareIcon}
                        onClick={onEdit}
                        title="Edit widget"
                        size="tiny"
                    />}
                </div>
            </div>
        }
    </div>;
}

export function Insights() {
    const is_sidebar_large = useSelector(selectIsSidebarLarge);
    const memberships = useSelector(selectMemberships);

    // loaded state from backend
    const [widgets, setWidgets] = useState<IWidget[] | undefined>(undefined);
    const [indicator_widgets, setIndicatorWidgets] = useState<IWidget[] | undefined>(undefined);
    const [lookup_tables, setLookupTables] = useState<ILookupTableBase[] | undefined>(undefined);
    // loaded state from localStorage
    const [expanded_widget_ids, _setExpandedWidgetIds] = useState<Set<string>>(new Set<string>(JSON.parse(localStorage.getItem("expanded_widget_ids") ?? "[]")));
    // widget edit/delete
    const [show_widget_side_panel, setShowWidgetSidePanel] = useState<boolean>(false);
    const [edit_widget_uuid, setEditWidgetUuid] = useState<string | undefined>(undefined);
    const [clone_widget_uuid, setCloneWidgetUuid] = useState<string | undefined>(undefined);
    const [delete_widget_uuid, setDeleteWidgetUuid] = useState<string | undefined>(undefined);
    // pinboard status
    const [pinned_widgets, setPinnedWidgets] = useState<IPinboardElement[] | undefined>(undefined);
    // rest
    const [error_message, setErrorMessage] = useState<string | undefined>(undefined);

    const fetchWidgets = useCallback(async () => {
        const { widgets: new_widgets } = await BackendObj.extractions.listWidgets({});
        setWidgets(new_widgets.filter(widget => !INDICATOR_WIDGET_TYPES.includes(widget.type)));
        setIndicatorWidgets(new_widgets.filter(widget => INDICATOR_WIDGET_TYPES.includes(widget.type)));
    }, []);

    useEffect(() => {
        fetchWidgets();
        // fetch lookup tables
        const fetchLookupTables = async () => {
            const { lookup_tables: new_lookup_tables } = await BackendObj.extractions.getLookupTables({});
            setLookupTables(new_lookup_tables);
        };
        fetchLookupTables();
        const fetchPinnedWidgets = async () => {
            const { elements } = await BackendObj.extractions.listPinboardElements({ pinned_type: "widget" });
            setPinnedWidgets(elements);
        };
        fetchPinnedWidgets();
    }, [fetchWidgets]);

    const removeWidget = async (result: boolean) => {
        if (delete_widget_uuid === undefined) { return; }
        if (result) {
            try {
                await BackendObj.extractions.deleteWidget({ widget_uuid: delete_widget_uuid });
                fetchWidgets();
            } catch (error) {
                setErrorMessage(error instanceof Error ? error.message : "Unknown error");
            }
        }
        setDeleteWidgetUuid(undefined);
    }

    if (widgets === undefined || indicator_widgets === undefined || lookup_tables === undefined) {
        return <div className={classNames("hidden lg:fixed lg:right-0 lg:h-16 lg:flex lg:flex-row border-b-sea_blue-700 border-b", is_sidebar_large ? "lg:left-64" : "lg:left-20")}>
            <LoadingSpinner />
        </div>;
    }

    const setExpandedWidgetIds = (new_expanded_widget_ids: Set<string>) => {
        localStorage.setItem("expanded_widget_ids", JSON.stringify(Array.from(new_expanded_widget_ids)));
        _setExpandedWidgetIds(new_expanded_widget_ids);
    };

    const toggleWidget = async (widget_uuid: string) => {
        const new_expanded_widget_ids = new Set(expanded_widget_ids);
        if (expanded_widget_ids.has(widget_uuid)) {
            new_expanded_widget_ids.delete(widget_uuid);
        } else {
            new_expanded_widget_ids.add(widget_uuid);
        }
        setExpandedWidgetIds(new_expanded_widget_ids);
    };

    const isWidgetPinned = (widget_uuid: string): boolean => {
        if (pinned_widgets === undefined) { return false; }
        return pinned_widgets.some(widget => widget.pinned_uuid === widget_uuid);
    };

    const isWidgetPinEditable = (widget_uuid: string): boolean => {
        if (pinned_widgets === undefined) { return false; }
        // if widget not pinned then it is editable
        if (pinned_widgets.find(widget => widget.pinned_uuid === widget_uuid) === undefined) { return true; }
        // if widget is pinned by user then it is editable
        if (pinned_widgets.some(widget => widget.pinned_uuid === widget_uuid && widget.level === "user")) { return true; }
        // if widget is pinned by org then it is not editable
        if (pinned_widgets.some(widget => widget.pinned_uuid === widget_uuid && widget.level === "org")) { return false; }
        // default to not editable
        return false;
    };

    const toggleWidgetPin = async (widget_uuid: string) => {
        if (pinned_widgets === undefined) { return; }
        if (!isWidgetPinEditable(widget_uuid)) { return; }
        // do the toggle
        if (pinned_widgets.some(widget => widget.pinned_uuid === widget_uuid)) {
            await BackendObj.extractions.removeElementFromPinboard({ pinned_uuid: widget_uuid });
        } else {
            await BackendObj.extractions.addElementToPinboard({ pinned_uuid: widget_uuid, pinned_type: "widget" });
        }
        // update the pinned widgets
        const { elements: new_pinned_widgets } = await BackendObj.extractions.listPinboardElements({ pinned_type: "widget" });
        setPinnedWidgets(new_pinned_widgets);
    };

    // Separate expanded and collapsed widgets
    const expanded_widgets = widgets.filter(widget => expanded_widget_ids.has(widget.uuid));
    const collapsed_widgets = widgets.filter(widget => !expanded_widget_ids.has(widget.uuid));

    // sort expanded widgets by so that wide widgets are only on even positions
    // for that we go from start of the array and swap wide widgets with the next one
    // wide widgets count as two positions
    let position = 0;
    for (let i = 0; i < expanded_widgets.length; i += 1) {
        if (isWidgetWide(expanded_widgets[i]) && position % 2 === 1 && i + 1 < expanded_widgets.length) {
            const temp = expanded_widgets[i];
            expanded_widgets[i] = expanded_widgets[i + 1];
            expanded_widgets[i + 1] = temp;
            position += 1;
        } else if (isWidgetWide(expanded_widgets[i])) {
            position += 2;
        } else {
            position += 1;
        }
    }

    return <div className={classNames("flex-row lg:fixed lg:right-0 lg:inset-y-0 overflow-y-scroll", is_sidebar_large ? "lg:left-64" : "lg:left-20")}>
        {(indicator_widgets.length === 0 && widgets.length === 0) ?
            <div className="flex justify-center items-center h-full w-full">
                <div className="text-center">
                    <RocketLaunchIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-semibold text-gray-900">No insights</h3>
                    <p className="mt-1 text-sm text-gray-500">Get started with by creating your first insight.</p>
                    <div className="mt-6">
                        <Button
                            icon={PlusIcon}
                            text="New Insight"
                            onClick={() => { setEditWidgetUuid(undefined); setCloneWidgetUuid(undefined); setShowWidgetSidePanel(true); }}
                        />
                    </div>
                </div>
            </div > :
            <Fragment>
                <div className="h-16 w-full bg-white border-b border-b-sea_blue-700">
                    <div className="px-10 py-4 flex flex-row items-center">
                        <h2 className="flex-grow text-xl font-semibold leading-7 text-gray-600 sm:truncate sm:text-2xl sm:tracking-tight">
                            Insights
                        </h2>
                        <Button
                            icon={PlusIcon}
                            text="New"
                            highlight={true}
                            onClick={() => { setEditWidgetUuid(undefined); setCloneWidgetUuid(undefined); setShowWidgetSidePanel(true); }}
                        />
                    </div>
                </div>


                <div className="px-10 py-6">
                    <div className="space-y-6">
                        {indicator_widgets.length > 0 && (<div className="py-4 flex flex-col w-full ">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
                                {indicator_widgets.map(widget => <IndicatorWidget
                                    key={widget.uuid}
                                    widget={widget}
                                    is_pinned={isWidgetPinned(widget.uuid)}
                                    is_editable={isWidgetPinEditable(widget.uuid)}
                                    onPinToggle={() => toggleWidgetPin(widget.uuid)}
                                    onRemove={() => setDeleteWidgetUuid(widget.uuid)}
                                    onEdit={() => { setEditWidgetUuid(widget.uuid); setShowWidgetSidePanel(true); }}
                                />)}
                            </div>
                        </div>)}

                        {expanded_widgets.length > 0 && (<div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                            {expanded_widgets.map((widget) => (<Widget
                                key={widget.uuid}
                                widget={widget}
                                show_icon={true}
                                is_pinned={isWidgetPinned(widget.uuid)}
                                is_editable={isWidgetPinEditable(widget.uuid)}
                                lookup_tables={lookup_tables}
                                onClose={() => toggleWidget(widget.uuid)}
                                onPinToggle={() => toggleWidgetPin(widget.uuid)}
                                onEdit={() => { setEditWidgetUuid(widget.uuid); setShowWidgetSidePanel(true); }}
                            />))}
                        </div>)}

                        {collapsed_widgets.length > 0 && <Fragment>
                            <div className="pt-6 text-lg font-medium text-gray-700">Other Insight Widgets</div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                                {collapsed_widgets.map((widget) => (
                                    <div key={widget.uuid} className="bg-white rounded-lg shadow overflow-hidden border border-gray-200 hover:border-gray-300 transition-colors">
                                        <div className="px-4 py-3 flex justify-between items-center cursor-pointer hover:bg-sea_blue-50" onClick={() => toggleWidget(widget.uuid)}>
                                            <div className="flex flex-row items-center gap-x-2">
                                                <WidgetIcon widget_type={widget.type} />
                                                <h3 className="text-md font-medium text-gray-700 truncate">{widget.title}</h3>
                                            </div>
                                            <ChevronUpIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
                                        </div>

                                        <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 text-xs text-gray-500">
                                            <div className="flex flex-row items-center gap-x-2">
                                                <span className="truncate">{getWidgetSource(widget, lookup_tables, memberships)}</span>
                                                <div className="flex-grow" />
                                                <IconButton
                                                    icon={PencilSquareIcon}
                                                    onClick={() => { setEditWidgetUuid(widget.uuid); setShowWidgetSidePanel(true); }}
                                                    title="Edit widget"
                                                    size="tiny"
                                                />
                                                <IconButton
                                                    icon={TrashIcon}
                                                    onClick={() => setDeleteWidgetUuid(widget.uuid)}
                                                    title="Delete widget"
                                                    size="tiny"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Fragment>}
                    </div>
                </div>
            </Fragment>}

        <WidgetSidePanel
            open={show_widget_side_panel}
            all_lookup_tables={lookup_tables}
            widget_uuid={edit_widget_uuid}
            clone_widget_uuid={clone_widget_uuid}
            onClose={(widget_uuid) => {
                if (widget_uuid !== undefined) { fetchWidgets(); }
                setEditWidgetUuid(undefined);
                setCloneWidgetUuid(undefined);
                setShowWidgetSidePanel(false);
            }}
            onRemove={(widget_uuid) => { setDeleteWidgetUuid(widget_uuid); setShowWidgetSidePanel(false); }}
            onClone={(widget_uuid) => { setEditWidgetUuid(undefined); setCloneWidgetUuid(widget_uuid); setShowWidgetSidePanel(true); }}
        />

        <ConfirmModal
            open={delete_widget_uuid !== undefined}
            title="Remove Widget"
            message={["Are you sure you want to remove this widget?"]}
            confirm="Remove"
            onClose={removeWidget} />

        <TimeoutErrorMessageBar
            message={error_message}
            clearMessage={() => setErrorMessage(undefined)}
            is_sidebar_large_override={true}
        />
    </div>;
}